import { LevelConfig } from '../config.ts';
import { Language } from '../core/Storage.ts';
import { getTranslation } from '../i18n.ts';
import { GIRL_MODELS } from '../entities/ItemFactory.ts';

export class UIManager {
  private hudElement: HTMLElement;
  private menuOverlay: HTMLElement;
  private gameOverOverlay: HTMLElement;
  private pauseOverlay: HTMLElement;
  private scoreValue: HTMLElement;
  private highScoreValue: HTMLElement;
  private livesContainer: HTMLElement;
  private levelBadge: HTMLElement;
  private comboBadge: HTMLElement;
  private muteBtn: HTMLElement;
  private pauseBtn: HTMLElement;
  private finalScore: HTMLElement;
  private finalLevel: HTMLElement;
  private bestScoreElem: HTMLElement;
  private newRecordBadge: HTMLElement;
  private modelsContainer: HTMLElement | null = null;

  // Rules & Score modal elements
  private rulesModal: HTMLElement | null = null;
  private openRulesBtn: HTMLElement | null = null;
  private topRulesBtn: HTMLElement | null = null;
  private closeRulesBtn: HTMLElement | null = null;
  private modalStartBtn: HTMLElement | null = null;
  private pauseRulesBtn: HTMLElement | null = null;
  private scoreModal: HTMLElement | null = null;
  private topScoreBtn: HTMLElement | null = null;
  private closeScoreBtn: HTMLElement | null = null;
  private scoreModalOkBtn: HTMLElement | null = null;
  private analyticsToggleBtn: HTMLButtonElement | null = null;
  private analyticsToggleLabel: HTMLElement | null = null;
  private analyticsToggleDesc: HTMLElement | null = null;
  private analyticsToggleStatus: HTMLElement | null = null;
  private isAnalyticsEnabled: boolean = true;

  private selectedGirlId: string = 'random';
  private currentLang: Language = 'ru';

  public onLanguageSelect?: (lang: Language) => void;
  public onGirlSelect?: (modelId: string) => void;
  public onAnalyticsToggle?: (enabled: boolean) => void;
  public onRulesOpen?: () => void;
  public onScoreOpen?: () => void;

  constructor() {
    this.hudElement = document.getElementById('hud')!;
    this.menuOverlay = document.getElementById('menu-overlay')!;
    this.gameOverOverlay = document.getElementById('gameover-overlay')!;
    this.pauseOverlay = document.getElementById('pause-overlay')!;
    this.scoreValue = document.getElementById('score-val')!;
    this.highScoreValue = document.getElementById('highscore-val')!;
    this.livesContainer = document.getElementById('lives-container')!;
    this.levelBadge = document.getElementById('level-badge')!;
    this.comboBadge = document.getElementById('combo-badge')!;
    this.muteBtn = document.getElementById('mute-btn')!;
    this.pauseBtn = document.getElementById('pause-btn')!;
    this.finalScore = document.getElementById('final-score')!;
    this.finalLevel = document.getElementById('final-level')!;
    this.bestScoreElem = document.getElementById('best-score')!;
    this.newRecordBadge = document.getElementById('new-record-badge')!;
    this.modelsContainer = document.getElementById('models-chips-container');

    // Rules & Score modals
    this.rulesModal = document.getElementById('rules-modal');
    this.openRulesBtn = document.getElementById('open-rules-btn');
    this.topRulesBtn = document.getElementById('top-rules-btn');
    this.closeRulesBtn = document.getElementById('close-rules-btn');
    this.modalStartBtn = document.getElementById('modal-start-btn');
    this.pauseRulesBtn = document.getElementById('pause-rules-btn');
    this.scoreModal = document.getElementById('score-modal');
    this.topScoreBtn = document.getElementById('top-score-btn');
    this.closeScoreBtn = document.getElementById('close-score-btn');
    this.scoreModalOkBtn = document.getElementById('score-modal-ok-btn');
    this.analyticsToggleBtn = document.getElementById('analytics-toggle-btn') as HTMLButtonElement | null;
    this.analyticsToggleLabel = document.getElementById('analytics-toggle-label');
    this.analyticsToggleDesc = document.getElementById('analytics-toggle-desc');
    this.analyticsToggleStatus = document.getElementById('analytics-toggle-status');

    this.setupLanguageButtons();
    this.setupModelSelector();
    this.setupRulesModal();
  }

  private setupLanguageButtons(): void {
    const langBtns = document.querySelectorAll<HTMLButtonElement>('.lang-btn');
    langBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const lang = btn.dataset.lang as Language;
        if ((lang === 'ru' || lang === 'en') && this.onLanguageSelect) {
          this.onLanguageSelect(lang);
        }
      });
    });
  }

  private setupRulesModal(): void {
    this.openRulesBtn?.addEventListener('click', () => this.openRules());
    this.topRulesBtn?.addEventListener('click', () => this.openRules());
    this.closeRulesBtn?.addEventListener('click', () => this.closeRules());
    this.pauseRulesBtn?.addEventListener('click', () => this.openRules());

    this.topScoreBtn?.addEventListener('click', () => this.openScoreModal());
    this.closeScoreBtn?.addEventListener('click', () => this.closeScoreModal());
    this.scoreModalOkBtn?.addEventListener('click', () => this.closeScoreModal());

    this.analyticsToggleBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.isAnalyticsEnabled = !this.isAnalyticsEnabled;
      this.updateAnalyticsUI();
      if (this.onAnalyticsToggle) {
        this.onAnalyticsToggle(this.isAnalyticsEnabled);
      }
    });

    this.rulesModal?.addEventListener('click', (e) => {
      if (e.target === this.rulesModal) {
        this.closeRules();
      }
    });

    this.scoreModal?.addEventListener('click', (e) => {
      if (e.target === this.scoreModal) {
        this.closeScoreModal();
      }
    });
  }

  public isRulesOpen(): boolean {
    return Boolean(
      (this.rulesModal && !this.rulesModal.classList.contains('hidden')) ||
      (this.scoreModal && !this.scoreModal.classList.contains('hidden'))
    );
  }

  public setAnalyticsEnabled(enabled: boolean): void {
    this.isAnalyticsEnabled = enabled;
    this.updateAnalyticsUI();
  }

  public updateAnalyticsUI(): void {
    if (!this.analyticsToggleBtn) return;
    const t = getTranslation(this.currentLang);
    this.analyticsToggleBtn.classList.toggle('active', this.isAnalyticsEnabled);
    this.analyticsToggleBtn.setAttribute('aria-checked', this.isAnalyticsEnabled ? 'true' : 'false');
    if (this.analyticsToggleStatus) {
      this.analyticsToggleStatus.textContent = this.isAnalyticsEnabled ? t.analyticsOn : t.analyticsOff;
    }
  }

  public openScoreModal(): void {
    this.closeRules();
    this.scoreModal?.classList.remove('hidden');
    if (this.onScoreOpen) {
      this.onScoreOpen();
    }
  }

  public closeScoreModal(): void {
    this.scoreModal?.classList.add('hidden');
  }

  public openRules(): void {
    this.closeScoreModal();
    this.rulesModal?.classList.remove('hidden');
    if (this.onRulesOpen) {
      this.onRulesOpen();
    }
  }

  public closeRules(): void {
    this.rulesModal?.classList.add('hidden');
    this.scoreModal?.classList.add('hidden');
  }

  public applyLanguage(lang: Language): void {
    const t = getTranslation(lang);
    document.title = t.docTitle;
    document.documentElement.lang = lang;

    const setHtml = (id: string, html: string) => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = html;
    };

    const setText = (id: string, text: string) => {
      const el = document.getElementById(id);
      if (el) el.textContent = text;
    };

    setText('menu-badge', t.badge18);
    setText('menu-title', t.gameTitle);
    setText('menu-tagline', t.tagline);

    // Old rule desc fallbacks if present in DOM
    setHtml('rule-girl-desc', t.ruleGirlDesc || t.ruleGirl);
    setHtml('rule-penis-desc', t.rulePenisDesc || t.rulePenis);
    setHtml('rule-grenade-desc', t.ruleGrenadeDesc || t.ruleGrenade);
    setHtml('rule-monkey-desc', t.ruleMonkeyDesc || t.ruleMonkey);
    setHtml('rule-dog-desc', t.ruleDogDesc || t.ruleDog);

    // Rules modal specific elements
    setText('rules-btn-text', t.rulesBtn);
    setText('rules-btn-subtext', t.rulesBtn);
    setText('rules-modal-title', t.rulesModalTitle);
    setText('rules-subtitle', t.rulesSubtitle);
    setText('rule-girl-name', t.ruleGirlName);
    setText('rule-penis-name', t.rulePenisName);
    setText('rule-grenade-name', t.ruleGrenadeName);
    setText('rule-monkey-name', t.ruleMonkeyName);
    setText('rule-dog-name', t.ruleDogName);
    setText('controls-hint-title', t.controlsTitle);
    setHtml('controls-mobile-text', t.controlsMobile);
    setHtml('controls-pc-text', t.controlsPC);
    if (this.analyticsToggleLabel) {
      this.analyticsToggleLabel.textContent = t.analyticsLabel;
    }
    if (this.analyticsToggleDesc) {
      this.analyticsToggleDesc.textContent = t.analyticsDesc;
    }
    this.updateAnalyticsUI();
    setText('modal-start-btn-text', t.modalStartBtn);

    // Buttons & Labels
    setText('start-btn-text', t.startBtn);
    setText('menu-highscore-label', t.menuHighscoreLabel);
    setText('score-modal-title', t.scoreModalTitle);
    setText('score-modal-ok-text', t.scoreModalOkBtn);
    setText('hud-top-label', t.hudTopLabel);

    this.pauseBtn.title = t.pauseBtnTitle;
    this.muteBtn.title = t.muteBtnTitle;
    const menuMuteBtn = document.getElementById('menu-mute-btn');
    if (menuMuteBtn) {
      menuMuteBtn.title = t.muteBtnTitle;
    }
    const topRulesBtn = document.getElementById('top-rules-btn');
    if (topRulesBtn) {
      topRulesBtn.title = t.rulesBtn;
    }
    if (this.topScoreBtn) {
      this.topScoreBtn.title = t.scoreBtnTitle;
      this.topScoreBtn.setAttribute('aria-label', t.scoreBtnTitle);
    }

    setText('pause-title', t.pauseTitle);
    setText('resume-btn-text', t.resumeBtn);
    setText('pause-rules-btn-text', t.pauseRulesBtn);
    setText('exit-btn-text', t.exitBtn);

    setText('gameover-title', t.gameOverTitle);
    setText('new-record-badge', t.newRecord);
    setText('stat-score-label', t.statScore);
    setText('stat-level-label', t.statLevel);
    setText('stat-best-label', t.statBest);
    setText('restart-btn-text', t.restartBtn);
    setText('gameover-menu-btn-text', t.exitBtn);

    this.currentLang = lang;
    setText('models-title', t.modelsTitle);
    setText('models-gallery-link', t.modelsGallery);
    if (this.modelsContainer) {
      this.modelsContainer.setAttribute('aria-label', t.modelsTitle);
    }
    this.renderModelChips();

    const langBtns = document.querySelectorAll<HTMLButtonElement>('.lang-btn');
    langBtns.forEach((btn) => {
      const isActive = btn.dataset.lang === lang;
      if (isActive) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  private setupModelSelector(): void {
    if (!this.modelsContainer) return;
    this.renderModelChips();
  }

  public setSelectedGirl(modelId: string): void {
    this.selectedGirlId = modelId;
    this.updateActiveChip();
  }

  private renderModelChips(): void {
    if (!this.modelsContainer) return;
    this.modelsContainer.innerHTML = '';
    const t = getTranslation(this.currentLang);

    GIRL_MODELS.forEach((model) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      const isActive = model.id === this.selectedGirlId;
      btn.className = `model-chip ${isActive ? 'active' : ''}`;
      btn.dataset.modelId = model.id;
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      const label = (t as unknown as Record<string, string>)[model.nameKey] || model.id;

      if (model.url) {
        btn.innerHTML = `<img src="${model.url}" class="model-chip-img" alt="${model.id}" /> <span>${label}</span>`;
      } else {
        btn.innerHTML = `<span style="font-size: 16px;">${model.icon || '🎲'}</span> <span>${label}</span>`;
      }

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectedGirlId = model.id;
        this.updateActiveChip();
        if (this.onGirlSelect) {
          this.onGirlSelect(model.id);
        }
      });

      this.modelsContainer!.appendChild(btn);
    });
  }

  private updateActiveChip(): void {
    if (!this.modelsContainer) return;
    const chips = this.modelsContainer.querySelectorAll<HTMLButtonElement>('.model-chip');
    chips.forEach((chip) => {
      const isActive = chip.dataset.modelId === this.selectedGirlId;
      chip.classList.toggle('active', isActive);
      chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) {
        chip.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });
  }

  public updateHUD(
    score: number,
    highScore: number,
    lives: number,
    level: LevelConfig,
    comboMultiplier: number = 1,
    levelName?: string
  ): void {
    this.scoreValue.textContent = score.toString();
    this.highScoreValue.textContent = highScore.toString();
    this.levelBadge.textContent = levelName || level.name;
    this.levelBadge.style.color = level.accentColor;

    if (comboMultiplier > 1) {
      this.comboBadge.textContent = `x${comboMultiplier} COMBO!`;
      this.comboBadge.classList.remove('hidden');
    } else {
      this.comboBadge.classList.add('hidden');
    }

    let livesHtml = '';
    for (let i = 0; i < 3; i++) {
      if (i < lives) {
        livesHtml += '<span class="life-icon full">❤️</span>';
      } else {
        livesHtml += '<span class="life-icon lost">🖤</span>';
      }
    }
    this.livesContainer.innerHTML = livesHtml;
  }

  public showMenu(highScore: number, onStart: () => void, onToggleMute: () => void, isMuted: boolean): void {
    this.closeRules();
    this.hudElement.classList.add('hidden');
    this.gameOverOverlay.classList.add('hidden');
    this.pauseOverlay.classList.add('hidden');
    this.menuOverlay.classList.remove('hidden');

    const menuHighscore = document.getElementById('menu-highscore')!;
    if (menuHighscore) {
      menuHighscore.textContent = highScore.toString();
    }

    const startBtn = document.getElementById('start-btn')!;
    if (startBtn) {
      startBtn.onclick = () => onStart();
    }

    if (this.modalStartBtn) {
      this.modalStartBtn.onclick = () => {
        this.closeRules();
        onStart();
      };
    }

    const menuMuteBtn = document.getElementById('menu-mute-btn');
    if (menuMuteBtn) {
      menuMuteBtn.onclick = () => onToggleMute();
    }

    this.updateMuteIcon(isMuted);
    this.muteBtn.onclick = () => onToggleMute();
  }

  public showHUD(onPause: () => void): void {
    this.closeRules();
    this.menuOverlay.classList.add('hidden');
    this.gameOverOverlay.classList.add('hidden');
    this.pauseOverlay.classList.add('hidden');
    this.hudElement.classList.remove('hidden');

    this.pauseBtn.onclick = () => onPause();
  }

  public showPause(onResume: () => void, onExitMenu: () => void): void {
    this.closeRules();
    this.pauseOverlay.classList.remove('hidden');

    const resumeBtn = document.getElementById('resume-btn')!;
    if (resumeBtn) {
      resumeBtn.onclick = () => {
        this.closeRules();
        onResume();
      };
    }

    const exitBtn = document.getElementById('exit-btn')!;
    if (exitBtn) {
      exitBtn.onclick = () => {
        this.closeRules();
        onExitMenu();
      };
    }
  }

  public hidePause(): void {
    this.closeRules();
    this.pauseOverlay.classList.add('hidden');
  }

  public showGameOver(
    score: number,
    levelName: string,
    bestScore: number,
    isNewRecord: boolean,
    onRestart: () => void,
    onExitMenu?: () => void
  ): void {
    this.closeRules();
    this.hudElement.classList.add('hidden');
    this.menuOverlay.classList.add('hidden');
    this.pauseOverlay.classList.add('hidden');
    this.gameOverOverlay.classList.remove('hidden');

    this.finalScore.textContent = score.toString();
    this.finalLevel.textContent = levelName;
    this.bestScoreElem.textContent = bestScore.toString();

    if (isNewRecord) {
      this.newRecordBadge.classList.remove('hidden');
    } else {
      this.newRecordBadge.classList.add('hidden');
    }

    const restartBtn = document.getElementById('restart-btn')!;
    if (restartBtn) {
      restartBtn.onclick = () => onRestart();
    }

    const menuBtn = document.getElementById('gameover-menu-btn');
    if (menuBtn && onExitMenu) {
      menuBtn.onclick = () => onExitMenu();
    }
  }

  public updateMuteIcon(isMuted: boolean): void {
    const icon = isMuted ? '🔇' : '🔊';
    this.muteBtn.textContent = icon;
    const menuMuteBtn = document.getElementById('menu-mute-btn');
    if (menuMuteBtn) {
      menuMuteBtn.textContent = icon;
    }
  }
}
